import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { TopShopRubricCard } from "./TopShopRubricCard";
import { ADMIN_LISTS_KEY, ListRow, PARISHES } from "./useTopShopAdmin";

type TableName = "program_business_types" | "program_communities" | "program_focus_areas";
const NO_PARISH = "__none";

function EditableRow({ row, table, withParish, onChanged, onMove, first, last }: {
  row: ListRow; table: TableName; withParish?: boolean; onChanged: () => void;
  onMove: (dir: -1 | 1) => void; first: boolean; last: boolean;
}) {
  const [name, setName] = useState(row.name);
  useEffect(() => setName(row.name), [row.name]);

  const update = async (patch: Record<string, unknown>) => {
    const { error } = await supabase.from(table).update(patch as any).eq("id", row.id);
    if (error) toast.error(error.message); else onChanged();
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b py-2 last:border-0">
      <Input
        aria-label="Name"
        className="flex-1 min-w-[160px]"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => { const v = name.trim(); if (v && v !== row.name) update({ name: v }); else setName(row.name); }}
      />
      {withParish && (
        <Select value={row.parish ?? NO_PARISH} onValueChange={(v) => update({ parish: v === NO_PARISH ? null : v })}>
          <SelectTrigger className="w-[150px]" aria-label="Parish"><SelectValue placeholder="Parish" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_PARISH}>No parish</SelectItem>
            {PARISHES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {row.is_catch_all && <Badge variant="secondary">Catch-all</Badge>}
      <div className="flex items-center gap-1.5">
        <Switch checked={!!row.is_active} onCheckedChange={(v) => update({ is_active: v })} aria-label="Active" />
        <span className="text-xs text-muted-foreground w-12">{row.is_active ? "Active" : "Hidden"}</span>
      </div>
      <Button size="icon" variant="ghost" disabled={first || !!row.is_catch_all} onClick={() => onMove(-1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" disabled={last || !!row.is_catch_all} onClick={() => onMove(1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
    </div>
  );
}

function ListCard({ title, note, rows: rawRows, table, programId, withParish }: {
  title: string; note?: string; rows: ListRow[]; table: TableName; programId: string; withParish?: boolean;
}) {
  const regularRows = rawRows.filter((r) => !r.is_catch_all);
  const rows = [...regularRows, ...rawRows.filter((r) => r.is_catch_all)];
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newParish, setNewParish] = useState(NO_PARISH);
  const [adding, setAdding] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ADMIN_LISTS_KEY });
    qc.invalidateQueries({ queryKey: ["top-shop"] });
  };

  const move = async (index: number, dir: -1 | 1) => {
    const a = rows[index], b = rows[index + dir];
    if (!a || !b) return;
    const aOrder = a.sort_order ?? index, bOrder = b.sort_order ?? index + dir;
    const swapA = aOrder === bOrder ? bOrder + dir : bOrder;
    const [r1, r2] = await Promise.all([
      supabase.from(table).update({ sort_order: swapA }).eq("id", a.id),
      supabase.from(table).update({ sort_order: aOrder }).eq("id", b.id),
    ]);
    if (r1.error || r2.error) toast.error((r1.error ?? r2.error)!.message);
    refresh();
  };

  const add = async () => {
    const v = newName.trim();
    if (!v) return;
    setAdding(true);
    const next = regularRows.reduce((m, r) => Math.max(m, r.sort_order ?? 0), 0) + 1;
    const payload: any = { program_id: programId, name: v, sort_order: next, is_active: true };
    if (withParish) payload.parish = newParish === NO_PARISH ? null : newParish;
    const { error } = await supabase.from(table).insert(payload);
    setAdding(false);
    if (error) { toast.error(error.message); return; }
    setNewName(""); setNewParish(NO_PARISH);
    toast.success("Added");
    refresh();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {note && <CardDescription>{note}</CardDescription>}
      </CardHeader>
      <CardContent>
        {rows.length === 0 && <p className="text-sm text-muted-foreground pb-2">No entries yet.</p>}
        {rows.map((r, i) => (
          <EditableRow key={r.id} row={r} table={table} withParish={withParish} onChanged={refresh}
            onMove={(d) => move(i, d)} first={i === 0} last={i >= regularRows.length - 1} />
        ))}
        <div className="flex flex-wrap items-center gap-2 pt-3">
          <Input aria-label={`New ${title} name`} placeholder="Add new" className="flex-1 min-w-[160px]" value={newName}
            onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          {withParish && (
            <Select value={newParish} onValueChange={setNewParish}>
              <SelectTrigger className="w-[150px]" aria-label="Parish"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PARISH}>No parish</SelectItem>
                {PARISHES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={add} disabled={adding || !newName.trim()}>
            {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function TopShopListsTab({ programId, lists }: {
  programId: string; lists?: { types: ListRow[]; communities: ListRow[]; focusAreas: ListRow[] };
}) {
  if (!lists) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <ListCard title="Types of shop" rows={lists.types} table="program_business_types" programId={programId} />
      <ListCard title="Communities" rows={lists.communities} table="program_communities" programId={programId} withParish />
      <ListCard title="Focus areas" rows={lists.focusAreas} table="program_focus_areas" programId={programId}
        note="Scores are entered per focus area. Deactivating an area hides it from the application form and scoring." />
      <TopShopRubricCard focusAreas={lists.focusAreas} />
    </div>
  );
}
